import { api } from './client';
import type { PaymentMode } from './bookings';

/**
 * Checkout: what a basket costs, and turning that basket into bookings.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * The cart used to price itself. `CartScreen` read a hardcoded `SERVICE_FEE`
 * and `TAX_RATE` out of `src/data/booking.ts`, matched a typed code against a
 * hardcoded coupon list, and did the arithmetic - item total, minus a coupon
 * the server had never heard of, plus a fee and a tax rate that were never the
 * server's own - right there in the component. Every number on the checkout
 * bar was the app's opinion of what something cost, not the platform's.
 *
 * This module is the fix: the server prices the basket, and the app renders
 * what it is told. `quoteCheckout` is the READ - call it on every change to
 * the basket, the typed code, the coins or the tip, and draw its `lines` and
 * `totalAmount` verbatim. `placeCheckout` is the WRITE - the one call that
 * turns a priced basket into bookings, replacing the old per-service loop in
 * `createBookings`.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE CALL, NOT A LOOP
 * ---------------------------------------------------------------------------
 * `createBookings` fans a basket out into one `POST /bookings` per service,
 * because that route only ever understood a single service and a single
 * price. Checkout is an order that can span several services, priced and
 * discounted as one basket - a coupon's minimum, a subscription's discount and
 * the coin ceiling all apply to the ORDER, not to one line of it - so pricing
 * has to happen once, over everything, not once per service with the discount
 * applied to whichever line went first.
 */

/** One row of the itemized bill - see `CheckoutQuoteDto.lines`. */
export type CheckoutLine = {
  label: string;
  /**
   * A decimal string, signed: negative is a discount, positive is a charge.
   *
   * Render these DIRECTLY, in order, as the bill. This is already the
   * finished breakdown - Items, Fee, Subscription, Coupon, Coins, Tip, each
   * one aggregated across the whole basket - not a per-service itemization,
   * and not something to recombine into a total of your own.
   */
  amount: string;
};

/** Why a typed code did not apply. Present only when a `code` was sent. */
export type CodeRefusal = {
  /** Machine-readable - `BELOW_MINIMUM`, `EXPIRED`, `ALREADY_USED`, etc. */
  reason: string;
  /** Customer-facing, verbatim. Show it next to the code field as it stands. */
  message: string;
};

/** One line in the basket being priced or placed. */
export type CheckoutItem = {
  serviceId: string;
  /** Hours bought, on a service sold by the hour - see `data/hourly.ts`. */
  hours?: number;
  /**
   * ISO instant: this service's own visit time, when the customer gave it
   * one. Omitted, the line follows the basket's `slotStartAt`. Still paid
   * together; each line becomes its own booking at its own time.
   */
  slotStartAt?: string;
};

export type CheckoutRequest = {
  items: CheckoutItem[];
  addressId: string;
  /** ISO instant. Omit for an instant booking - never derive one locally. */
  slotStartAt?: string;
  /** Whatever is currently in the coupon field - typed or picked from the
      list. Omitted rather than sent empty, so an untouched field asks for no
      code at all instead of asking the server to refuse `""`. */
  code?: string;
  /** A count, never a rupee figure - the server prices coins itself. */
  coinsToRedeem?: number;
  /** A decimal string, e.g. `"50.00"`. */
  tipAmount?: string;
};

/** `POST checkout/quote`'s answer - the finished bill for one basket. */
export type CheckoutQuoteDto = {
  lines: CheckoutLine[];
  itemsSubtotal: string;
  serviceFeeAmount: string;
  /** Subscription + coupon + coins, combined. */
  discountAmount: string;
  coinsRedeemed: number;
  walletDiscountAmount: string;
  tipAmount: string;
  /** What the customer pays. Tax-inclusive - there is no tax line to add. */
  totalAmount: string;
  /** Present only when a `code` was sent and it did not apply. */
  codeRefusal?: CodeRefusal;
  /** The code that actually applied, or set when an automatic offer did with
      nothing typed. Absent when nothing applied. */
  appliedCode?: string | null;
  /** Most coins this basket could redeem, whether or not any were asked for
      this time - the ceiling the "Use Homingo Coins" row offers. */
  maxRedeemableCoins: number;
  /**
   * Coins a voucher would leave over once this basket is paid for.
   *
   * A preview, not a promise about this bill: they are locked until every
   * booking in the order is done, and the final count is measured against the
   * bookings that actually happened. Zero whenever nothing carries over.
   *
   * **Optional**, because a deployment older than the feature does not send
   * it - read it as `?? 0` rather than assuming a number.
   */
  voucherRemainderCoins?: number;
};

/**
 * The price of a basket, as it stands right now.
 *
 * A PURE READ - nothing is reserved, nothing is charged, and it is safe to
 * call on every keystroke, every coupon apply, every coin toggle and every tip
 * tap. Debounce the CALLER, not this: the endpoint itself does no writing and
 * has nothing to protect against being asked twice.
 */
export const quoteCheckout = (input: CheckoutRequest, signal?: AbortSignal) =>
  api.post<CheckoutQuoteDto>('/checkout/quote', input, { signal });

export type PlaceCheckoutRequest = CheckoutRequest & {
  paymentMode: PaymentMode;
  /**
   * The customer ticked the booking terms: any trained professional may be
   * assigned, and refusing them at the door is not a refund. Optional on the
   * wire so an older server simply ignores it.
   */
  acceptedTerms?: boolean;
  /**
   * The basket will be paid as one, through `checkouts/:id/payment`, so the
   * server does not mint a gateway order per booking at placement.
   */
  singlePayment?: boolean;
};

/** `POST checkout`'s answer - the order actually placed. */
export type CheckoutDto = {
  id: string;
  /**
   * One id per item that actually got booked.
   *
   * ---------------------------------------------------------------------------
   * SHORTER THAN `items` IS A PARTIAL SUCCESS, NOT A FAILURE
   * ---------------------------------------------------------------------------
   * If one line in a multi-item basket fails to book - a service went
   * inactive in the seconds since the quote, say - the others still succeed.
   * This is the same philosophy `createBookings` documents for the old
   * per-item flow: rejecting everything because one line could not be booked
   * is cheaper for nobody, and the customer is charged only for what actually
   * got dispatched. Treat a short list as a partial success to communicate,
   * never as a reason to retry the whole order.
   */
  bookingIds: string[];
  /** Frozen, not a preview - the actual figures this order was charged. */
  itemsSubtotal: string;
  serviceFeeAmount: string;
  discountAmount: string;
  coinsRedeemed: number;
  walletDiscountAmount: string;
  tipAmount: string;
  totalAmount: string;
  /**
   * `awaiting_payment` for a basket paid as one: NOTHING is booked yet and
   * `bookingIds` is empty until the payment is captured. `placed` once the
   * bookings exist.
   */
  status?: 'awaiting_payment' | 'paid' | 'placed' | 'expired';
  /** What the one payment charges, while it is awaited. */
  payableAmount?: string | null;
};

/**
 * Places the order - the one call that replaces the old `createBookings` loop.
 *
 * Same body as `quoteCheckout`, plus `paymentMode`. Send an `Idempotency-Key`:
 * a retried request must return the order already placed rather than booking
 * it a second time, the same rule `createBooking` enforces for a single
 * service - see that function's own note in `services/bookings.ts`.
 */
export const placeCheckout = (
  input: PlaceCheckoutRequest,
  idempotencyKey: string,
) =>
  api.post<CheckoutDto>('/checkout', input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });

/**
 * The basket's units, each carrying its service's own time when it has one.
 *
 * `times` is by service - every unit of a service shares its visit - and a
 * service missing from it gets nothing added, so it follows the basket's
 * `slotStartAt`. A basket nobody split therefore sends exactly the items it
 * always did, and an older backend is never shown a field it would refuse.
 */
export const withServiceTimes = (
  items: readonly CheckoutItem[],
  times: Readonly<Record<string, string>>,
): CheckoutItem[] =>
  items.map(item => {
    const own = times[item.serviceId];
    return own ? { ...item, slotStartAt: own } : item;
  });

/**
 * What "Proceed to Pay" places: the basket as the customer agreed to it,
 * online, paid BEFORE anything is booked.
 *
 * Never `cash`. This used to follow `onlinePaymentsEnabled()`, which is also
 * false before the app's config has loaded or when that request fails - and
 * cash is booked on the spot and dispatched, so a customer who never paid got
 * a live "Pay after service" booking. A booking exists only once the money
 * does; a server that cannot take a payment says so and books nothing.
 *
 * `items`, `code`, `tipAmount` and `coinsToRedeem` are exactly what the cart's
 * last quote showed - re-deriving any of them here could place the order at a
 * price the customer never saw.
 */
export const basketPlacement = (
  order: {
    items: CheckoutItem[];
    slotStartAt?: string;
    code?: string;
    tipAmount?: string;
    coinsToRedeem?: number;
    acceptedTerms?: boolean;
  },
  addressId: string,
): PlaceCheckoutRequest => ({
  items: order.items,
  addressId,
  paymentMode: 'online',
  singlePayment: true,
  slotStartAt: order.slotStartAt,
  code: order.code,
  tipAmount: order.tipAmount,
  coinsToRedeem: order.coinsToRedeem,
  /*
   * The terms the customer ticked in the cart. A flag, not a time: the
   * server stamps the clock, because a phone's own is not evidence of
   * anything - and it is what a refund argument will be answered with.
   */
  acceptedTerms: order.acceptedTerms,
});
